import React, { useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    FaTimes,
    FaSyncAlt,
    FaSearch,
    FaLayerGroup,
    FaChevronLeft,
    FaChevronRight,
} from "react-icons/fa";
import { useLeaderReportList } from "../hooks/useLeaderReportList";
import { LEADER_REPORT_LIST_TITLES } from "../constants/leaderReportList";

const LEVELS = [1, 2, 3, 4, 5, 6];

function safeText(v) {
    return v === null || v === undefined || v === "" ? "—" : String(v);
}

function nfmt2(v) {
    const num = Number(v);
    if (!Number.isFinite(num)) return "0.00";
    return new Intl.NumberFormat("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(num);
}

function formatDateCompact(v) {
    if (!v) return { date: "—", time: "" };
    const dt = new Date(v);
    if (!Number.isFinite(dt.getTime())) return { date: safeText(v), time: "" };
    return {
        date: dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" }),
        time: dt.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true }),
    };
}

function DateCell({ value }) {
    const { date, time } = formatDateCompact(value);
    return (
        <td className="col-date-cell">
            <div className="date-line">{date}</div>
            {time ? <div className="time-line text-muted">{time}</div> : null}
        </td>
    );
}

function userMobile(u) {
    const mobile = safeText(u?.mobile);
    const cc = u?.country_code;
    if (mobile === "—") return "—";
    const ccStr = cc === null || cc === undefined || cc === "" ? "" : String(cc);
    return `+${ccStr} ${mobile}`.trim();
}

function UserCell({ user }) {
    if (!user) return <span className="text-muted">—</span>;
    return (
        <div className="small">
            <div className="fw-semibold">{safeText(user.name)}</div>
            <div className="text-muted">ID {safeText(user.user_id)}</div>
            <div className="text-muted">{userMobile(user)}</div>
            <div className="text-muted text-truncate" style={{ maxWidth: 200 }} title={safeText(user.email)}>
                {safeText(user.email)}
            </div>
        </div>
    );
}

function serialNo(pageNo, limit, index) {
    return (Math.max(1, pageNo) - 1) * limit + index + 1;
}

function LoadingRow({ cols }) {
    return (
        <tr>
            {Array.from({ length: cols }).map((_, i) => (
                <td key={i} className="px-3 py-3">
                    <div className="placeholder-glow">
                        <span className="placeholder col-10" />
                    </div>
                </td>
            ))}
        </tr>
    );
}

function PaginationBar({ loading, pageNo, totalPages, onPrev, onNext, onJump }) {
    const pages = useMemo(() => {
        const n = Number(totalPages || 1);
        return Array.from({ length: n }, (_, i) => i + 1);
    }, [totalPages]);

    return (
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
            <div className="text-muted small">
                Page <span className="fw-semibold">{pageNo}</span> of{" "}
                <span className="fw-semibold">{totalPages}</span>
            </div>
            <div className="d-flex align-items-center gap-2">
                <button
                    className="btn btn-outline-secondary btn-sm px-3 py-1 d-inline-flex align-items-center gap-2"
                    onClick={onPrev}
                    disabled={loading || pageNo <= 1}
                    type="button"
                >
                    <FaChevronLeft /> Prev
                </button>
                <button
                    className="btn btn-outline-secondary btn-sm px-3 py-1 d-inline-flex align-items-center gap-2"
                    onClick={onNext}
                    disabled={loading || pageNo >= totalPages}
                    type="button"
                >
                    Next <FaChevronRight />
                </button>
                <div className="d-flex align-items-center gap-2 ms-2">
                    <span className="text-muted small">Go to</span>
                    <select
                        className="form-select form-select-sm"
                        value={pageNo}
                        onChange={(e) => onJump(Number(e.target.value))}
                        disabled={loading || totalPages <= 1}
                        style={{ width: "70px", padding: "0.25rem 0.5rem" }}
                    >
                        {pages.map((p) => (
                            <option key={p} value={p}>
                                {p}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
        </div>
    );
}

function TradeTable({ list, pageNo, limit, loading }) {
    const cols = 9;
    return (
        <table className="table table-sm table-hover align-middle mb-0 leader-report-table">
            <colgroup>
                <col style={{ width: "44px" }} />
                <col style={{ width: "92px" }} />
                <col style={{ width: "11%" }} />
                <col style={{ width: "9%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "58px" }} />
                <col />
            </colgroup>
            <thead className="table-light sticky-top">
                <tr>
                    <th className="text-center">#</th>
                    <th>Date</th>
                    <th>Entity</th>
                    <th>Period</th>
                    <th className="text-end">Amount</th>
                    <th className="text-end">Win</th>
                    <th className="text-end">Profit</th>
                    <th className="text-center">St</th>
                    <th className="col-user-cell">User</th>
                </tr>
            </thead>
            <tbody>
                {loading ? (
                    <>
                        <LoadingRow cols={cols} />
                        <LoadingRow cols={cols} />
                        <LoadingRow cols={cols} />
                        <LoadingRow cols={cols} />
                        <LoadingRow cols={cols} />
                    </>
                ) : (
                    list.map((row, idx) => {
                        const isWin = String(row.status) === "1";
                        const profit = Number(row.profit);
                        return (
                            <tr key={row.trade_id ?? idx}>
                                <td className="text-muted small text-center">{serialNo(pageNo, limit, idx)}</td>
                                <DateCell value={row.create_date} />
                                <td className="small text-truncate" title={safeText(row.entity?.entity_name)}>
                                    {safeText(row.entity?.entity_name)}
                                </td>
                                <td className="small">
                                    <div className="text-truncate" title={safeText(row.period)}>
                                        {safeText(row.period)}
                                    </div>
                                    <div className="text-muted text-truncate" style={{ fontSize: "0.7rem" }}>
                                        {safeText(row.side)}
                                    </div>
                                </td>
                                <td className="text-end fw-semibold text-nowrap">{nfmt2(row.trade_amount)}</td>
                                <td className="text-end text-nowrap">{nfmt2(row.win_amount)}</td>
                                <td
                                    className={`text-end fw-bold text-nowrap ${
                                        profit >= 0 ? "text-success" : "text-danger"
                                    }`}
                                >
                                    {nfmt2(profit)}
                                </td>
                                <td className="text-center">
                                    <span
                                        className={`badge ${isWin ? "text-bg-success" : "text-bg-danger"}`}
                                        style={{ fontSize: "0.65rem" }}
                                    >
                                        {isWin ? "W" : "L"}
                                    </span>
                                </td>
                                <td className="col-user-cell">
                                    <UserCell user={row.user} />
                                </td>
                            </tr>
                        );
                    })
                )}
            </tbody>
        </table>
    );
}

function FirstDepositTable({ list, pageNo, limit, loading }) {
    const cols = 6;
    return (
        <table className="table table-sm table-hover align-middle mb-0 leader-report-table">
            <colgroup>
                <col style={{ width: "44px" }} />
                <col style={{ width: "92px" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "13%" }} />
                <col />
            </colgroup>
            <thead className="table-light sticky-top">
                <tr>
                    <th className="text-center">#</th>
                    <th>Date</th>
                    <th className="text-end">USDT</th>
                    <th className="text-end">Coin</th>
                    <th className="text-end">Comm.</th>
                    <th className="col-user-cell">User</th>
                </tr>
            </thead>
            <tbody>
                {loading ? (
                    <>
                        <LoadingRow cols={cols} />
                        <LoadingRow cols={cols} />
                        <LoadingRow cols={cols} />
                        <LoadingRow cols={cols} />
                        <LoadingRow cols={cols} />
                    </>
                ) : (
                    list.map((row, idx) => (
                        <tr key={row.swap_id ?? idx}>
                            <td className="text-muted small text-center">{serialNo(pageNo, limit, idx)}</td>
                            <DateCell value={row.create_date} />
                            <td className="text-end text-nowrap">{nfmt2(row.usdt)}</td>
                            <td className="text-end text-nowrap">{nfmt2(row.coin)}</td>
                            <td className="text-end fw-semibold text-success text-nowrap">
                                {nfmt2(row.commission_amount)}
                            </td>
                            <td className="col-user-cell">
                                <UserCell user={row.user} />
                            </td>
                        </tr>
                    ))
                )}
            </tbody>
        </table>
    );
}

function SwapTable({ list, pageNo, limit, loading }) {
    const cols = 5;
    return (
        <table className="table table-sm table-hover align-middle mb-0 leader-report-table">
            <colgroup>
                <col style={{ width: "44px" }} />
                <col style={{ width: "92px" }} />
                <col style={{ width: "14%" }} />
                <col style={{ width: "14%" }} />
                <col />
            </colgroup>
            <thead className="table-light sticky-top">
                <tr>
                    <th className="text-center">#</th>
                    <th>Date</th>
                    <th className="text-end">USDT</th>
                    <th className="text-end">Coin</th>
                    <th className="col-user-cell">User</th>
                </tr>
            </thead>
            <tbody>
                {loading ? (
                    <>
                        <LoadingRow cols={cols} />
                        <LoadingRow cols={cols} />
                        <LoadingRow cols={cols} />
                        <LoadingRow cols={cols} />
                        <LoadingRow cols={cols} />
                    </>
                ) : (
                    list.map((row, idx) => (
                        <tr key={row.swap_id ?? idx}>
                            <td className="text-muted small text-center">{serialNo(pageNo, limit, idx)}</td>
                            <DateCell value={row.create_date} />
                            <td className="text-end text-nowrap">{nfmt2(row.usdt)}</td>
                            <td className="text-end text-nowrap">{nfmt2(row.coin)}</td>
                            <td className="col-user-cell">
                                <UserCell user={row.user} />
                            </td>
                        </tr>
                    ))
                )}
            </tbody>
        </table>
    );
}

function ListBody({ variant, list, loading, pageNo, limit }) {
    if (!loading && list.length === 0) {
        return (
            <div className="text-center py-4 text-muted">
                <FaSearch size={24} className="mb-2 opacity-50" />
                <div className="fw-semibold">No records found</div>
                <div className="small">Try another level or search term.</div>
            </div>
        );
    }
    if (variant === "trade") {
        return <TradeTable list={list} pageNo={pageNo} limit={limit} loading={loading} />;
    }
    if (variant === "first-deposit") {
        return <FirstDepositTable list={list} pageNo={pageNo} limit={limit} loading={loading} />;
    }
    return <SwapTable list={list} pageNo={pageNo} limit={limit} loading={loading} />;
}

/**
 * @param {{
 *   open: boolean,
 *   variant: import("../types/leaderReportList").LeaderReportListVariant | null,
 *   onClose: () => void,
 *   leaderUsername: string,
 *   fromDate: string,
 *   toDate: string,
 *   initialLevel?: number,
 *   onSessionExpired?: () => void,
 * }} props
 */
export default function LeaderReportListModal({
    open,
    variant,
    onClose,
    leaderUsername,
    fromDate,
    toDate,
    initialLevel = 1,
    onSessionExpired,
}) {
    const {
        list,
        info,
        loading,
        error,
        level,
        searchInput,
        setSearchInput,
        onLevelChange,
        refresh,
        pageNo,
        totalPages,
        onPrev,
        onNext,
        onJump,
    } = useLeaderReportList({
        variant,
        open,
        leaderUsername,
        fromDate,
        toDate,
        initialLevel,
        onSessionExpired,
    });

    const limit = Number(info?.limit || 20);

    useEffect(() => {
        if (!open) return undefined;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = prev;
        };
    }, [open]);

    const titleBase = variant ? LEADER_REPORT_LIST_TITLES[variant] : "List";
    const title = `${titleBase} — Level ${level}`;

    return (
        <AnimatePresence>
            {open && variant ? (
                <>
                    <style>{`
                        .leader-report-modal-outer {
                            overflow: hidden !important;
                            scrollbar-width: none;
                            -ms-overflow-style: none;
                        }
                        .leader-report-modal-outer::-webkit-scrollbar {
                            display: none;
                            width: 0;
                            height: 0;
                        }
                        .leader-report-modal-scroll {
                            flex: 1 1 auto;
                            min-height: 0;
                            overflow: auto;
                            scrollbar-width: none;
                            -ms-overflow-style: none;
                        }
                        .leader-report-modal-table-wrap {
                            flex: 1 1 auto;
                            min-height: 0;
                            display: flex;
                            flex-direction: column;
                        }
                        .leader-report-toolbar .btn-sm {
                            padding: 0.2rem 0.55rem;
                            font-size: 0.8rem;
                        }
                        .leader-report-table {
                            table-layout: fixed;
                            width: 100%;
                        }
                        .leader-report-modal-scroll .table td,
                        .leader-report-modal-scroll .table th {
                            padding: 0.35rem 0.5rem;
                            overflow: hidden;
                            vertical-align: middle;
                        }
                        .col-date-cell {
                            line-height: 1.2;
                            max-width: 92px;
                        }
                        .col-date-cell .date-line {
                            font-size: 0.75rem;
                            font-weight: 600;
                            white-space: nowrap;
                        }
                        .col-date-cell .time-line {
                            font-size: 0.68rem;
                            white-space: nowrap;
                        }
                        .leader-report-table th.col-user-cell,
                        .leader-report-table td.col-user-cell {
                            padding-left: 1rem !important;
                            border-left: 1px solid rgba(15, 23, 42, 0.06);
                        }
                        .col-user-cell {
                            min-width: 160px;
                            word-break: break-word;
                        }
                        .leader-report-modal-scroll::-webkit-scrollbar {
                            display: none;
                            width: 0;
                            height: 0;
                        }
                        .level-btn-active {
                            box-shadow: 0 0 0 2px rgba(13,110,253,.35);
                        }
                    `}</style>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="modal-backdrop show d-block"
                        style={{
                            backgroundColor: "rgba(0,0,0,0.5)",
                            position: "fixed",
                            inset: 0,
                            zIndex: 1040,
                        }}
                        onClick={onClose}
                    />
                    <div
                        className="modal show d-block leader-report-modal-outer"
                        tabIndex={-1}
                        style={{ position: "fixed", inset: 0, zIndex: 1050, overflow: "hidden" }}
                    >
                        <motion.div
                            initial={{ opacity: 0, scale: 0.96, y: -12 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.96, y: -12 }}
                            transition={{ duration: 0.18 }}
                            className="modal-dialog modal-dialog-centered modal-xl my-2"
                            onClick={(e) => e.stopPropagation()}
                            style={{ maxWidth: "96vw", height: "calc(100vh - 1rem)", margin: "0.5rem auto" }}
                        >
                            <div
                                className="modal-content border-0 shadow-lg d-flex flex-column h-100"
                                style={{ borderRadius: 14 }}
                            >
                                <div
                                    className="modal-header border-bottom py-2 px-3 flex-shrink-0"
                                    style={{
                                        background:
                                            "linear-gradient(135deg, rgba(13,110,253,.08), rgba(32,201,151,.06))",
                                    }}
                                >
                                    <div className="me-2 overflow-hidden">
                                        <h6 className="modal-title fw-bold mb-0 text-truncate">{title}</h6>
                                        <div className="small text-muted" style={{ fontSize: "0.75rem" }}>
                                            {fromDate} → {toDate}
                                            {info?.total != null ? (
                                                <span className="ms-1">
                                                    · <strong>{info.total}</strong> total
                                                </span>
                                            ) : null}
                                        </div>
                                    </div>
                                    <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
                                </div>

                                <div className="leader-report-toolbar flex-shrink-0 px-3 py-2 border-bottom">
                                    <div className="d-flex flex-wrap align-items-center gap-2">
                                        <div className="d-flex align-items-center gap-1 flex-shrink-0">
                                            <span className="text-muted small text-nowrap">
                                                <FaLayerGroup className="me-1" />
                                                Level
                                            </span>
                                            {LEVELS.map((n) => (
                                                <button
                                                    key={n}
                                                    type="button"
                                                    className={`btn btn-sm ${
                                                        level === n
                                                            ? "btn-primary level-btn-active"
                                                            : "btn-outline-secondary"
                                                    }`}
                                                    onClick={() => onLevelChange(n)}
                                                >
                                                    {n}
                                                </button>
                                            ))}
                                        </div>
                                        <input
                                            type="text"
                                            className="form-control form-control-sm flex-grow-1"
                                            style={{ minWidth: "140px" }}
                                            placeholder="Search name, id, mobile, email…"
                                            value={searchInput}
                                            onChange={(e) => setSearchInput(e.target.value)}
                                        />
                                        <button
                                            type="button"
                                            className="btn btn-outline-primary btn-sm flex-shrink-0 d-inline-flex align-items-center gap-1"
                                            onClick={refresh}
                                            disabled={loading}
                                        >
                                            <FaSyncAlt className={loading ? "fa-spin" : ""} />
                                            Refresh
                                        </button>
                                    </div>
                                    {error ? (
                                        <div className="alert alert-danger py-1 px-2 small mb-0 mt-2">{error}</div>
                                    ) : null}
                                </div>

                                <div
                                    className="leader-report-modal-table-wrap px-3 py-2 flex-grow-1"
                                    style={{ minHeight: 0 }}
                                >
                                    <div className="border rounded-2 leader-report-modal-scroll h-100">
                                        <ListBody
                                            variant={variant}
                                            list={list}
                                            loading={loading}
                                            pageNo={pageNo}
                                            limit={limit}
                                        />
                                    </div>
                                </div>

                                <div className="modal-footer border-top px-3 py-2 flex-shrink-0 d-flex flex-wrap align-items-center justify-content-between gap-2">
                                    <PaginationBar
                                        loading={loading}
                                        pageNo={pageNo}
                                        totalPages={totalPages}
                                        onPrev={onPrev}
                                        onNext={onNext}
                                        onJump={onJump}
                                    />
                                    <button type="button" className="btn btn-secondary btn-sm flex-shrink-0" onClick={onClose}>
                                        <FaTimes className="me-1" />
                                        Close
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </>
            ) : null}
        </AnimatePresence>
    );
}
